import { NetworkType, ParameterParsedValue } from '@mls-wallet/shared-types';
import type { AbiParameter } from 'viem';
import type { AddressBookExactEntry } from '@/types/address-book';

/**
 * Enum representing the validation state of the bytes string.
 *
 * Used to drive UI helper/error messaging while editing bytes-like inputs.
 */
export enum BytesValidationState {
  VALID,
  VALID_BUT_EMPTY,
  INVALID_NOT_BYTES,
  INVALID_NOT_HEX_STRING,
  INVALID_INCORRECT_LENGTH,
}

/**
 * Enum representing the validation state of a string.
 */
export enum StringValidationState {
  VALID,
  VALID_BUT_EMPTY,
  INVALID,
}

/**
 * Enum representing the validation state of a number input.
 *
 * This is intentionally granular to support "intermediate" typing states
 * (e.g. user typing "-" before a negative number).
 */
export enum NumberValidationState {
  EMPTY,
  VALID,
  INVALID_CANT_BE_JUST_NEGATIVE,
  INVALID_CANT_BE_NEGATIVE,
  INVALID_DECIMALS,
  INVALID_CANT_END_WITH_DECIMAL,
}

/**
 * Type for a number input value as represented in the UI.
 *
 * - `value`: The user-facing value string (may include decimals and/or "-" while typing).
 * - `decimals`: The display decimals applied to `value`.
 * - `formatType`: Optional display mode. In datetime mode, the underlying value is epoch seconds.
 */
export interface NumberValueType {
  value: string;
  decimals: number;
  formatType?: 'number' | 'datetime';
}

export type TransactionBuilderParameterState = {
  isValid: boolean;
  isEmpty: boolean;
  value: string;
  parsedValue?: ParameterParsedValue | undefined;
};

type TransactionBuilderInputOnChangeHandler = (
  parameterName: string,
  newParameterState: TransactionBuilderParameterState
) => void;

type TransactionBuilderInputOnConfirmHandler = () => void;

/**
 * Props shared by all parameter input components in the transaction builder.
 *
 */
export type TransactionBuilderParameterInputProps = {
  value: string;
  unformattedValue: string;
  isEmpty: boolean;
  isValid: boolean;
  name: string;
  networkId: NetworkType;
  paramAbiFragment: AbiParameter;
  autoFocus?: boolean;
  onChange?: TransactionBuilderInputOnChangeHandler;
  onConfirm?: TransactionBuilderInputOnConfirmHandler;
  hideDecimalsButton?: boolean;
  hideAddressLabel?: boolean;
  /** Compact argument cards in the batch transaction dialog. */
  cardLayout?: boolean;
  allowUnsavedAddress?: boolean;
  addressBookEntryByAddress?: ReadonlyMap<string, AddressBookExactEntry>;
  addressBookLookupPending?: boolean;
  addressBookLookupFailed?: boolean;
};
